import { ApiRequestError, type TrafficEvidenceRun } from '@/services/api';

// The public route closes a long request (Cloudflare returns 524 after 100 s)
// while the server still completes the Evidence Run. The client then recovers
// the run from the saved run history instead of reporting a failure.
export const RUN_RECOVERY_POLL_INTERVAL_MS = 5_000;
export const RUN_RECOVERY_APPEAR_TIMEOUT_MS = 30_000;
export const RUN_RECOVERY_MAX_WAIT_MS = 60 * 60_000;

const TERMINAL_RUN_STATUSES: ReadonlySet<TrafficEvidenceRun['status']> = new Set([
  'complete',
  'failed',
  'interrupted',
  'cancelled',
]);

export function isTerminalTrafficEvidenceRun(run: TrafficEvidenceRun): boolean {
  return TERMINAL_RUN_STATUSES.has(run.status);
}

// A network failure or a gateway response can hide a run that the server
// accepted. An application error with a 4xx status is a real rejection.
export function isRecoverableRunStartFailure(error: unknown): boolean {
  if (error instanceof ApiRequestError) return error.status >= 500;
  return error instanceof TypeError;
}

// The form start route uses this correlation ID, so the client can find the
// exact run that its own request started (server/src/routes/traffic.ts).
export function formRunCorrelationId(draftId: string, draftRevision: number): string {
  return `traffic-form-${draftId}-${draftRevision}`;
}

export type TrafficEvidenceRunRecovery =
  | { outcome: 'finished'; run: TrafficEvidenceRun }
  | { outcome: 'still_running'; run: TrafficEvidenceRun }
  | { outcome: 'not_started' }
  | { outcome: 'history_unavailable' }
  | { outcome: 'cancelled' };

export async function recoverTrafficEvidenceRun(input: {
  listRuns: () => Promise<TrafficEvidenceRun[]>;
  knownRunIds: ReadonlySet<string>;
  requestCorrelationId: string;
  onRunFound?: (run: TrafficEvidenceRun) => void;
  sleep: (milliseconds: number) => Promise<void>;
  now: () => number;
  shouldContinue?: () => boolean;
  pollIntervalMs?: number;
  appearTimeoutMs?: number;
  maxWaitMs?: number;
}): Promise<TrafficEvidenceRunRecovery> {
  const shouldContinue = input.shouldContinue ?? (() => true);
  const pollIntervalMs = input.pollIntervalMs ?? RUN_RECOVERY_POLL_INTERVAL_MS;
  const appearTimeoutMs = input.appearTimeoutMs ?? RUN_RECOVERY_APPEAR_TIMEOUT_MS;
  const maxWaitMs = input.maxWaitMs ?? RUN_RECOVERY_MAX_WAIT_MS;
  const startedAt = input.now();
  let startedRun: TrafficEvidenceRun | null = null;
  let latestReadSucceeded = false;

  for (;;) {
    if (!shouldContinue()) return { outcome: 'cancelled' };
    try {
      const runs = await input.listRuns();
      latestReadSucceeded = true;
      const found = runs.find((run) => (
        !input.knownRunIds.has(run.id)
        && run.requestCorrelationId === input.requestCorrelationId
      ));
      if (found && !startedRun) input.onRunFound?.(found);
      startedRun = found ?? startedRun;
    } catch (error) {
      // A refused read (4xx) does not improve on retry. A network or gateway
      // failure can, because it goes through the same route as the start.
      if (!isRecoverableRunStartFailure(error)) return { outcome: 'history_unavailable' };
      latestReadSucceeded = false;
    }
    if (!shouldContinue()) return { outcome: 'cancelled' };
    if (startedRun && isTerminalTrafficEvidenceRun(startedRun)) {
      return { outcome: 'finished', run: startedRun };
    }

    // "Not started" needs a successful latest history read. A failed read
    // proves nothing about the run.
    const elapsed = input.now() - startedAt;
    if (!startedRun && latestReadSucceeded && elapsed >= appearTimeoutMs) return { outcome: 'not_started' };
    if (elapsed >= maxWaitMs) {
      return startedRun
        ? { outcome: 'still_running', run: startedRun }
        : { outcome: 'history_unavailable' };
    }
    await input.sleep(pollIntervalMs);
  }
}
